/**
 * jai1 tasks delete <ids...> [-y] [-j] [--group <name>]
 *
 * Modes:
 *   j t delete T-001              → single task
 *   j t delete T-001 T-002 T-003  → multi-task
 *   j t delete --group feature/x  → all tasks in group
 *
 * Safe delete: shows impact summary before confirming.
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { confirm } from '@inquirer/prompts';
import { TaskService } from '../../services/task.service.js';
import { STATUS_ICONS, type Task, type TaskDeleteOptions, type TaskStatusType } from '../../types/task.types.js';

/**
 * Display task list with status icons
 */
function printTaskList(tasks: Task[], indent = '  '): void {
    for (const t of tasks) {
        const icon = STATUS_ICONS[t.status as TaskStatusType] || '📋';
        console.log(`${indent}${icon} ${chalk.dim(t.id)} ${t.title}`);
    }
}

/**
 * Display impact summary for safe delete
 */
function printImpactSummary(tasksToDelete: Task[], allDependents: Task[]): void {
    // Status breakdown
    const inProgress = tasksToDelete.filter((t) => t.status === 'in_progress');
    const todo = tasksToDelete.filter((t) => t.status === 'todo');
    const done = tasksToDelete.filter((t) => t.status === 'done');
    const cancelled = tasksToDelete.filter((t) => t.status === 'cancelled');

    console.log(chalk.bold(`\n🗑️  Delete: ${tasksToDelete.length} task(s)\n`));

    // Warning for in_progress tasks
    if (inProgress.length > 0) {
        console.log(chalk.yellow.bold(`  ⚠️  ${inProgress.length} task(s) đang IN_PROGRESS:`));
        printTaskList(inProgress, '     ');
        console.log();
    }

    // Status breakdown
    const parts: string[] = [];
    if (todo.length > 0) parts.push(`${todo.length} todo`);
    if (inProgress.length > 0) parts.push(chalk.yellow(`${inProgress.length} in_progress`));
    if (done.length > 0) parts.push(`${done.length} done`);
    if (cancelled.length > 0) parts.push(`${cancelled.length} cancelled`);
    console.log(`  ${chalk.dim('Breakdown:')} ${parts.join(', ')}`);

    // Show all tasks
    console.log(`  ${chalk.dim('Tasks:')}`);
    printTaskList(tasksToDelete, '    ');
    console.log();

    // Dependency impact
    if (allDependents.length > 0) {
        const activeDependents = allDependents.filter(
            (t) => t.status !== 'done' && t.status !== 'cancelled'
        );

        console.log(
            chalk.yellow(`  ⚠️  ${allDependents.length} task(s) bên ngoài có depends_on sẽ bị ảnh hưởng:`)
        );
        printTaskList(allDependents, '     ');

        if (activeDependents.length > 0) {
            console.log(
                chalk.dim(`\n     → ${activeDependents.length} task(s) chưa done có thể bị unblock.`)
            );
        }
        console.log();
    }
}

export function createTaskDeleteCommand(): Command {
    return new Command('delete')
        .description('Delete task(s) or entire group permanently (with safe delete)')
        .argument('[ids...]', 'Task ID(s) (e.g. T-001 T-002 T-003)')
        .option('-g, --group <name>', 'Delete all tasks in a group (e.g. feature/auth)')
        .option('-y, --yes', 'Skip confirmation')
        .option('-j, --json', 'Output JSON')
        .action(async (ids: string[], options: TaskDeleteOptions) => {
            const service = new TaskService();

            try {
                let tasksToDelete: Task[];

                // === Mode: Group delete ===
                if (options.group) {
                    const allTasks = await service.readAll();
                    tasksToDelete = allTasks.filter((t) => t.parent === options.group);

                    if (tasksToDelete.length === 0) {
                        console.error(chalk.red(`❌ No tasks found in group: ${options.group}`));
                        process.exit(1);
                    }
                }
                // === Mode: Single or Multi-task delete ===
                else {
                    if (!ids || ids.length === 0) {
                        console.error(chalk.red('❌ Cần chỉ định task ID(s) hoặc --group <name>'));
                        console.error(chalk.dim('\nVí dụ:'));
                        console.error(chalk.dim('  j t delete T-001'));
                        console.error(chalk.dim('  j t delete T-001 T-002 T-003'));
                        console.error(chalk.dim('  j t delete --group feature/auth'));
                        process.exit(1);
                    }

                    // Validate all IDs exist
                    tasksToDelete = [];
                    for (const id of ids) {
                        const task = await service.findById(id);
                        if (!task) {
                            console.error(chalk.red(`❌ Task ${id} not found`));
                            process.exit(1);
                        }
                        tasksToDelete.push(task);
                    }
                }

                // Collect all external dependents (tasks that depend on any of the tasks to delete,
                // but are NOT themselves being deleted)
                const deleteIds = new Set(tasksToDelete.map((t) => t.id));
                const allDependents: Task[] = [];
                const seenDeps = new Set<string>();

                for (const task of tasksToDelete) {
                    const dependents = await service.getDependents(task.id);
                    for (const dep of dependents) {
                        // Only show external dependents (not being deleted themselves)
                        if (!deleteIds.has(dep.id) && !seenDeps.has(dep.id)) {
                            allDependents.push(dep);
                            seenDeps.add(dep.id);
                        }
                    }
                }

                // === Safe delete: show impact summary ===
                if (options.group) {
                    console.log(chalk.bold.cyan(`\n📦 Group: ${options.group}`));
                }
                printImpactSummary(tasksToDelete, allDependents);

                // Confirm
                if (!options.yes) {
                    const label = options.group
                        ? `Xoá vĩnh viễn group "${options.group}" (${tasksToDelete.length} tasks)?`
                        : tasksToDelete.length === 1
                            ? `Xoá vĩnh viễn task ${tasksToDelete[0]!.id}?`
                            : `Xoá vĩnh viễn ${tasksToDelete.length} task(s)?`;

                    const proceed = await confirm({
                        message: label,
                        default: false,
                    });
                    if (!proceed) {
                        console.log(chalk.dim('Đã huỷ.'));
                        return;
                    }
                }

                // Output JSON before deletion if requested
                if (options.json) {
                    console.log(JSON.stringify({
                        deleted: tasksToDelete,
                        dependentsUpdated: allDependents.map((d) => d.id),
                        group: options.group || null,
                    }, null, 2));
                }

                // Perform deletion
                const idsToDelete = tasksToDelete.map((t) => t.id);
                await service.deleteTasks(idsToDelete);

                if (!options.json) {
                    if (options.group) {
                        console.log(chalk.green(`✅ Đã xoá group "${options.group}" (${tasksToDelete.length} tasks)`));
                    } else if (tasksToDelete.length === 1) {
                        console.log(chalk.green(`✅ Đã xoá ${tasksToDelete[0]!.id}: ${tasksToDelete[0]!.title}`));
                    } else {
                        console.log(chalk.green(`✅ Đã xoá ${tasksToDelete.length} task(s): ${idsToDelete.join(', ')}`));
                    }

                    if (allDependents.length > 0) {
                        console.log(
                            chalk.dim(
                                `   Đã cập nhật depends_on cho: ${allDependents.map((d) => d.id).join(', ')}`
                            )
                        );
                    }
                }
            } catch (error) {
                console.error(
                    chalk.red(`❌ ${error instanceof Error ? error.message : String(error)}`)
                );
                process.exit(1);
            }
        });
}
